import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Surfaces unhandled errors to the user instead of leaving them in the console.
 *
 * Several failures — the place editor, dialog handlers, background lookups —
 * previously only reached `console.error`, so from the user's side the app just
 * silently did nothing.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private snackBar = inject(MatSnackBar);
  private zone = inject(NgZone);

  /** Avoids stacking identical toasts when an error repeats in a loop. */
  private lastMessage = '';
  private lastShownAt = 0;

  handleError(error: unknown): void {
    // Keep the full error in the console for debugging; the toast is a summary.
    console.error(error);

    const message = this.toUserMessage(error);
    const now = Date.now();
    if (message === this.lastMessage && now - this.lastShownAt < 5000) {
      return;
    }
    this.lastMessage = message;
    this.lastShownAt = now;

    // Errors can surface from outside Angular's zone; without this the snackbar
    // renders late or not at all.
    this.zone.run(() => {
      this.snackBar.open(message, 'Dismiss', { duration: 6000 });
    });
  }

  private toUserMessage(error: unknown): string {
    const raw = this.unwrap(error);

    // Firebase errors carry a machine-readable code; translate the ones a user
    // can actually act on and fall back to something honest for the rest.
    const code = typeof raw === 'object' && raw !== null ? (raw as { code?: string }).code : undefined;

    switch (code) {
      case 'permission-denied':
        return 'You do not have permission to do that.';
      case 'unauthenticated':
        return 'Your session expired. Please sign in again.';
      case 'unavailable':
      case 'deadline-exceeded':
        return 'Network problem. Check your connection and try again.';
      case 'not-found':
        return 'That item no longer exists.';
      case 'failed-precondition':
        return 'That action is not possible right now.';
      case 'resource-exhausted':
        return 'Too many requests. Please wait a moment and try again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  /** Angular wraps errors thrown in a subscription in a rejection object. */
  private unwrap(error: unknown): unknown {
    if (error && typeof error === 'object' && 'rejection' in error) {
      return (error as { rejection: unknown }).rejection;
    }
    return error;
  }
}
